import type { ChangeEvent, FormEvent } from 'react';
import { Alert, Avatar, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { PhotoCamera } from '@mui/icons-material';
import type { ProfileChoice, UserProfileData } from 'api-client';

export interface ProfileEditorProps {
  value: UserProfileData;
  languages: ProfileChoice[];
  timezones: ProfileChoice[];
  avatarUrl?: string;
  emailVerified: boolean;
  confirmedEmail: string;
  pendingEmail: string | null;
  pending: boolean;
  error: string | null;
  success: string | null;
  onChange: (value: UserProfileData) => void;
  onSubmit: (event: FormEvent) => void;
  onAvatar: (file: File) => void;
}
export default function ProfileEditorFields(props: ProfileEditorProps) {
  const { value, pending, onChange } = props;
  const change = (field: keyof UserProfileData) => (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, [field]: event.target.value });
  };
  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <Avatar src={props.avatarUrl} alt="Your profile picture" sx={{ width: 96, height: 96 }} />
        <Box>
          <Button component="label" variant="outlined" startIcon={<PhotoCamera />} disabled={pending}>
            Change picture
            <Box
              component="input"
              type="file"
              accept="image/*"
              aria-label="Profile picture"
              disabled={pending}
              sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clipPath: 'inset(50%)' }}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const file = event.target.files?.[0];
                if (file) props.onAvatar(file);
                event.target.value = '';
              }}
            />
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            PNG, JPEG or other supported image; up to 5 MB.
          </Typography>
        </Box>
      </Stack>
      {props.pendingEmail && (
        <Alert severity="info">
          Email confirmation is pending for {props.pendingEmail}. Your current address remains {props.confirmedEmail}.
        </Alert>
      )}
      <Box component="form" onSubmit={props.onSubmit}>
        <Stack spacing={2}>
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={value.email}
            disabled={pending}
            onChange={change('email')}
            helperText={
              value.email !== props.confirmedEmail
                ? 'Confirm the new address to finish changing your email.'
                : props.emailVerified
                  ? 'Current email address verified.'
                  : 'Verify your current email address.'
            }
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First Name"
              fullWidth
              value={value.firstName}
              disabled={pending}
              onChange={change('firstName')}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={value.lastName}
              disabled={pending}
              onChange={change('lastName')}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label="Language"
              fullWidth
              value={value.language}
              disabled={pending}
              onChange={change('language')}
              slotProps={{ select: { native: true } }}
            >
              {props.languages.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </TextField>
            <TextField
              select
              label="Timezone"
              fullWidth
              value={value.timezone}
              disabled={pending}
              onChange={change('timezone')}
              slotProps={{ select: { native: true } }}
            >
              {props.timezones.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </TextField>
          </Stack>
          {props.error && <Alert severity="error">{props.error}</Alert>}
          {props.success && <Alert severity="success">{props.success}</Alert>}
          <Button
            type="submit"
            variant="contained"
            loading={pending}
            loadingPosition="start"
            disabled={pending}
            sx={{ alignSelf: 'flex-start' }}
          >
            {pending ? 'Saving profile…' : 'Save Changes'}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
